import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useSelector } from "react-redux";

const CardForm = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useSelector((state) => state.user);

  const initialFormData = {
    id: "",
    branch: "",
    B: ["", "", "", "", ""],
    I: ["", "", "", "", ""],
    N: ["", "", "Free", "", ""],
    G: ["", "", "", "", ""],
    O: ["", "", "", "", ""],
  };
  const [formData, setFormData] = useState(initialFormData);

  const [branches, setBranches] = useState([]);
  const [branchPage, setBranchPage] = useState(1);
  const [hasMoreBranches, setHasMoreBranches] = useState(true);
  const [branchLoading, setBranchLoading] = useState(false);

  const fetchBranches = useCallback(async (page = 1, isLoadMore = false) => {
    try {
      setBranchLoading(true);
      setError(null);

      let response;
      const limit = 50;
      const params = new URLSearchParams({ page, limit }).toString();

      if (currentUser?.role === "superadmin") {
        response = await fetch(`/api/branch/branch?${params}`);
      } else {
        response = await fetch(`/api/branch/getbranch/${currentUser?.username}?${params}`);
      }

      if (!response.ok) throw new Error("Failed to fetch branches");

      const data = await response.json();
      const branchesData = data?.branches || data || [];

      if (isLoadMore) {
        setBranches(prev => [...prev, ...branchesData]);
      } else {
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      }

      setHasMoreBranches(branchesData.length >= limit);
      setBranchPage(page);
    } catch (err) {
      console.error("Branch fetch error:", err);
      setError("Error fetching branches");
      setBranches([]);
    } finally {
      setBranchLoading(false);
    }
  }, [currentUser]);

  const loadMoreBranches = () => {
    fetchBranches(branchPage + 1, true);
  };

  useEffect(() => {
    fetchBranches(1);
  }, [fetchBranches]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleArrayChange = (e, column, index) => {
    const { value } = e.target;

    if (value !== "" && (isNaN(value) || value < 1 || value > 99)) {
      return;
    }

    const updatedColumn = [...formData[column]];
    if (column === "N" && index === 2) {
      updatedColumn[index] = "Free";
    } else {
      updatedColumn[index] = value;
    }

    setFormData({
      ...formData,
      [column]: updatedColumn,
    });
  };

  const checkForRepeatedNumbers = () => {
    const allNumbers = [
      ...formData.B,
      ...formData.I,
      ...formData.G,
      ...formData.O,
      ...formData.N.filter((val, idx) => idx !== 2),
    ].filter((num) => num !== "");

    const uniqueNumbers = new Set(allNumbers);
    return uniqueNumbers.size !== allNumbers.length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (checkForRepeatedNumbers()) {
      alert("There are repeated numbers in the card. Please check and ensure all numbers are unique.");
      return;
    }

    const cardData = {
      id: formData.id,
      branch: formData.branch,
      card: {
        B: formData.B.map(num => num === "" ? 0 : Number(num)),
        I: formData.I.map(num => num === "" ? 0 : Number(num)),
        N: formData.N.map((val) => (val === "Free" ? val : val === "" ? 0 : Number(val))),
        G: formData.G.map(num => num === "" ? 0 : Number(num)),
        O: formData.O.map(num => num === "" ? 0 : Number(num)),
      },
    };

    try {
      setLoading(true);
      const response = await axios.post("/api/card/create", cardData);
      alert(response.data?.message || "Card created successfully!");
      setFormData({
        ...initialFormData,
        branch: formData.branch,
      });
    } catch (error) {
      alert(error.response?.data?.message || "Error creating card");
      console.error("Creation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderBranchOptions = () => {
    // Admin/employee: use their assigned comma-separated branches
    if (["admin", "employee"].includes(currentUser.role)) {
      return (currentUser.branch || "").split(",")
        .filter(branch => branch.trim() !== "")
        .map((branch, index) => (
          <option key={index} value={branch.trim()}>
            {branch.trim()}
          </option>
        ));
    }
    // Superadmin: use paginated branches endpoint
    if (currentUser.role === "superadmin") {
      return branches.map((branch) => (
        <option key={branch._id || branch.id} value={branch.name}>
          {branch.name}
        </option>
      ));
    }
    // Agent and other roles: use branches fetched for the agent (same endpoint as AgentDashboard)
    return branches.map((branch) => (
      <option key={branch._id || branch.id} value={branch.name}>
        {branch.name}
      </option>
    ));
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        margin: "20px",
        marginTop: "30px",
        fontFamily: "Arial, sans-serif",
        maxWidth: "500px",
        padding: "20px",
        border: "2px solid #2a2df5",
        borderRadius: "10px",
        backgroundColor: "#f9f9f9",
      }}
    >
      <div style={{ marginBottom: "15px" }}>
        <label style={{ marginRight: "10px" }}>ID:</label>
        <input
          type="number"
          name="id"
          value={formData.id}
          onChange={handleChange}
          required
          min="1"
          style={{
            padding: "10px",
            width: "calc(100% - 22px)",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        />
      </div>
      <div style={{ marginBottom: "15px" }}>
        <label htmlFor="branch" className="tw-text-lg tw-font-bold">
          Branch:
        </label>
        <div className="tw-relative">
          <select
            name="branch"
            onChange={handleChange}
            value={formData.branch}
            required
            disabled={loading}
            className="tw-dark:bg-slate-100 sm:tw-w-[390px] tw-rounded-lg tw-border tw-border-slate-300 tw-p-2.5"
          >
            <option value="">Select Branch</option>
            {renderBranchOptions()}
          </select>
          {hasMoreBranches && currentUser.role === "superadmin" && (
            <button
              onClick={loadMoreBranches}
              disabled={branchLoading}
              className="tw-absolute tw-right-1 tw-bottom-1 tw-px-2 tw-py-1 tw-bg-blue-600 tw-text-white tw-text-xs tw-rounded hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              {branchLoading ? 'Loading...' : 'Load More'}
            </button>
          )}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "15px",
        }}
      >
        {["B", "I", "N", "G", "O"].map((column) => (
          <div
            key={column}
            style={{ textAlign: "center", flex: "1", margin: "0 5px" }}
          >
            <label
              style={{
                fontSize: "18px",
                marginBottom: "10px",
                display: "block",
                color: "#2a2df5",
              }}
            >
              {column}
            </label>
            {formData[column].map((val, index) => (
              <input
                key={index}
                type={column === "N" && index === 2 ? "text" : "number"}
                value={column === "N" && index === 2 ? "Free" : val}
                onChange={(e) => handleArrayChange(e, column, index)}
                required={column !== "N" || index !== 2}
                readOnly={column === "N" && index === 2}
                disabled={loading}
                min="1"
                max="99"
                style={{
                  padding: "10px",
                  marginBottom: "5px",
                  width: "100%",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  backgroundColor:
                    column === "N" && index === 2 ? "#e0e0e0" : "white",
                }}
              />
            ))}
          </div>
        ))}
      </div>
      <button
        disabled={loading}
        type="submit"
        style={{
          padding: "10px 20px",
          backgroundColor: "#2a2df5",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          width: "100%",
          fontSize: "16px",
        }}
      >
        {loading ? "Loading..." : "Create Card"}
      </button>
      {error && <p className="tw-text-red-500 tw-mt-5">{error}</p>}
    </form>
  );
};

export default CardForm;